import { useEffect, useMemo, useState } from 'react';
import {
  Archive,
  CalendarPlus,
  CheckCircle2,
  History,
  Info,
  Lightbulb,
  Loader2,
  Plus,
  Save,
  Target,
  Trash2,
  Workflow,
  X,
} from 'lucide-react';
import {
  emptyScrumTiState,
  getScrumTiState,
  saveScrumTiState,
  type ScrumTiDay,
  type ScrumTiPoint,
  type ScrumTiPointStatus,
  type ScrumTiState,
} from '../services/scrumTiService';

type ScrumTIModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type ScrumTiTab = 'preparation' | 'fonctionnement' | 'historique' | 'archives';

const statusStyles: Record<ScrumTiPointStatus, string> = {
  'À valider': 'bg-amber-100 text-amber-800 border-amber-200',
  'À ajouter au scrum': 'bg-blue-100 text-blue-800 border-blue-200',
  Archivé: 'bg-slate-100 text-slate-700 border-slate-200',
};

function getTodayIso() {
  return new Date().toISOString().slice(0, 10);
}

function getCurrentWeekMondayIso() {
  const date = new Date();
  const day = date.getDay();
  const daysSinceMonday = day === 0 ? 6 : day - 1;
  date.setDate(date.getDate() - daysSinceMonday);
  return date.toISOString().slice(0, 10);
}

function formatDateLabel(value: string) {
  if (!value) return 'Sans date';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('fr-CA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function createId(prefix: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function ScrumTIModal({ isOpen, onClose }: ScrumTIModalProps) {
  const [state, setState] = useState<ScrumTiState>(() => emptyScrumTiState());
  const [selectedScrumDate, setSelectedScrumDate] = useState(getCurrentWeekMondayIso());
  const [activeTab, setActiveTab] = useState<ScrumTiTab>('preparation');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [owner, setOwner] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let ignore = false;
    setLoading(true);
    setError('');
    getScrumTiState()
      .then((payload) => {
        if (ignore) return;
        setState(payload.state);
        setUpdatedBy(payload.updated_by_email || '');
      })
      .catch((err) => {
        if (!ignore) setError(err instanceof Error ? err.message : 'Erreur chargement Scrum-TI');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [isOpen]);

  const visiblePoints = useMemo(
    () => state.points.filter((point) => point.status !== 'Archivé'),
    [state.points],
  );

  const archivedPoints = useMemo(
    () => state.points.filter((point) => point.status === 'Archivé'),
    [state.points],
  );

  const pointsById = useMemo(() => {
    return state.points.reduce<Record<string, ScrumTiPoint>>((acc, point) => {
      acc[point.id] = point;
      return acc;
    }, {});
  }, [state.points]);

  const sortedScrums = useMemo(() => {
    return [...state.scrums].sort((a, b) => b.date.localeCompare(a.date));
  }, [state.scrums]);

  const currentWeekScrumDate = useMemo(() => getCurrentWeekMondayIso(), []);
  const currentWeekScrum = useMemo(
    () => state.scrums.find((scrum) => scrum.date === currentWeekScrumDate) || null,
    [currentWeekScrumDate, state.scrums],
  );
  const currentWeekPoints = useMemo(() => {
    const pointIds = new Set(currentWeekScrum?.pointIds || []);
    state.points.forEach((point) => {
      if (point.status === 'À ajouter au scrum' && point.scrumDate === currentWeekScrumDate) {
        pointIds.add(point.id);
      }
    });
    return Array.from(pointIds)
      .map((pointId) => pointsById[pointId])
      .filter(Boolean);
  }, [currentWeekScrum, currentWeekScrumDate, pointsById, state.points]);

  if (!isOpen) return null;

  async function persist(nextState: ScrumTiState) {
    setState(nextState);
    setSaving(true);
    setError('');
    try {
      const payload = await saveScrumTiState(nextState);
      setState(payload.state);
      setUpdatedBy(payload.updated_by_email || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur sauvegarde Scrum-TI');
    } finally {
      setSaving(false);
    }
  }

  function handleAddPoint() {
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      setError('Le titre du point à discuter est requis.');
      return;
    }

    const now = new Date().toISOString();
    const point: ScrumTiPoint = {
      id: createId('point'),
      title: cleanTitle,
      details: details.trim(),
      owner: owner.trim(),
      status: 'À valider',
      scrumDate: '',
      createdAt: now,
      updatedAt: now,
    };

    void persist({
      ...state,
      points: [point, ...state.points],
    });
    setTitle('');
    setDetails('');
    setOwner('');
  }

  function updatePoint(pointId: string, updates: Partial<ScrumTiPoint>) {
    const now = new Date().toISOString();
    const nextPoints = state.points.map((point) =>
      point.id === pointId
        ? {
            ...point,
            ...updates,
            updatedAt: now,
          }
        : point,
    );
    void persist({
      ...state,
      points: nextPoints,
    });
  }

  function sendPointToScrum(pointId: string) {
    if (!selectedScrumDate) {
      setError('Choisis une journée de scrum avant d’ajouter le point.');
      return;
    }
    updatePoint(pointId, {
      status: 'À ajouter au scrum',
      scrumDate: selectedScrumDate,
      archivedAt: undefined,
    });
  }

  function archivePoint(pointId: string) {
    updatePoint(pointId, {
      status: 'Archivé',
      archivedAt: new Date().toISOString(),
    });
  }

  function deletePoint(pointId: string) {
    const nextPoints = state.points.filter((point) => point.id !== pointId);
    const nextScrums = state.scrums.map((scrum) => ({
      ...scrum,
      pointIds: scrum.pointIds.filter((id) => id !== pointId),
    }));
    void persist({
      points: nextPoints,
      scrums: nextScrums,
    });
  }

  function createScrumDay() {
    if (!selectedScrumDate) {
      setError('Choisis une date avant de créer la journée de scrum.');
      return;
    }

    const now = new Date().toISOString();
    const pointIdsForDay = state.points
      .filter((point) => point.status === 'À ajouter au scrum' && point.scrumDate === selectedScrumDate)
      .map((point) => point.id);
    const existing = state.scrums.find((scrum) => scrum.date === selectedScrumDate);
    const nextScrum: ScrumTiDay = existing
      ? {
          ...existing,
          pointIds: Array.from(new Set([...existing.pointIds, ...pointIdsForDay])),
          updatedAt: now,
        }
      : {
          id: createId('scrum'),
          date: selectedScrumDate,
          title: `Scrum-TI du ${formatDateLabel(selectedScrumDate)}`,
          pointIds: pointIdsForDay,
          createdAt: now,
          updatedAt: now,
        };

    void persist({
      ...state,
      scrums: existing
        ? state.scrums.map((scrum) => (scrum.id === existing.id ? nextScrum : scrum))
        : [nextScrum, ...state.scrums],
    });
  }

  function renderPointCard(point: ScrumTiPoint, compact = false) {
    return (
      <div key={point.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="font-bold text-slate-900">{point.title}</h4>
              <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusStyles[point.status]}`}>
                {point.status}
              </span>
            </div>
            {point.details && <p className="mt-2 text-sm leading-relaxed text-slate-600">{point.details}</p>}
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
              {point.owner && <span>Responsable: {point.owner}</span>}
              {point.scrumDate && <span>Scrum: {formatDateLabel(point.scrumDate)}</span>}
            </div>
          </div>

          {!compact && (
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => updatePoint(point.id, { status: 'À valider', scrumDate: '', archivedAt: undefined })}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-xs font-semibold text-amber-800 hover:bg-amber-100"
              >
                <CheckCircle2 className="h-4 w-4" />
                À valider
              </button>
              <button
                type="button"
                onClick={() => sendPointToScrum(point.id)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-800 hover:bg-blue-100"
              >
                <CalendarPlus className="h-4 w-4" />
                À ajouter au scrum
              </button>
              <button
                type="button"
                onClick={() => archivePoint(point.id)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                <Archive className="h-4 w-4" />
                Archiver
              </button>
              <button
                type="button"
                onClick={() => deletePoint(point.id)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 text-xs font-semibold text-red-700 hover:bg-red-100"
              >
                <Trash2 className="h-4 w-4" />
                Supprimer
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
      <div className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-2xl">
        <div className="flex items-start justify-between gap-4 bg-slate-950 px-6 py-5 text-white">
          <div>
            <h2 className="text-2xl font-black">Scrum-TI</h2>
            <p className="mt-1 text-sm text-slate-300">
              Points à discuter, préparation du scrum du lundi et historique des journées.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-white hover:bg-white/10"
            aria-label="Fermer Scrum-TI"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {loading ? (
            <div className="flex h-56 items-center justify-center text-slate-600">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Chargement Scrum-TI...
            </div>
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {error}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase text-slate-500">À valider</div>
                  <div className="mt-2 text-3xl font-black text-slate-900">
                    {state.points.filter((point) => point.status === 'À valider').length}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase text-slate-500">À ajouter au scrum</div>
                  <div className="mt-2 text-3xl font-black text-slate-900">
                    {state.points.filter((point) => point.status === 'À ajouter au scrum').length}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase text-slate-500">Scrums créés</div>
                  <div className="mt-2 text-3xl font-black text-slate-900">{state.scrums.length}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase text-slate-500">Archivés</div>
                  <div className="mt-2 text-3xl font-black text-slate-900">{archivedPoints.length}</div>
                </div>
              </div>

              <section className="rounded-xl border border-blue-200 bg-blue-50 p-5 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase text-blue-700">Scrum de la semaine en cours</div>
                    <h3 className="mt-1 text-2xl font-black text-slate-950">
                      {formatDateLabel(currentWeekScrumDate)}
                    </h3>
                    <p className="mt-1 text-sm text-slate-700">
                      {currentWeekScrum
                        ? `${currentWeekScrum.pointIds.length} point(s) officiellement attaché(s) à la journée.`
                        : 'La journée de scrum de cette semaine n’est pas encore créée.'}
                    </p>
                  </div>
                  <div className="rounded-lg bg-white px-4 py-3 text-center shadow-sm">
                    <div className="text-3xl font-black text-blue-800">{currentWeekPoints.length}</div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Point(s) visibles cette semaine</div>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {currentWeekPoints.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-blue-200 bg-white/70 p-4 text-sm text-slate-600 md:col-span-2">
                      Aucun point n’est associé au scrum de cette semaine.
                    </div>
                  ) : (
                    currentWeekPoints.map((point) => renderPointCard(point, true))
                  )}
                </div>
              </section>

              <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
                <button
                  type="button"
                  onClick={() => setActiveTab('preparation')}
                  className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold ${
                    activeTab === 'preparation'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <CalendarPlus className="h-4 w-4" />
                  Préparation
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('fonctionnement')}
                  className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold ${
                    activeTab === 'fonctionnement'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Info className="h-4 w-4" />
                  Fonctionnement
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('historique')}
                  className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold ${
                    activeTab === 'historique'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <History className="h-4 w-4" />
                  Historique des scrums
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('archives')}
                  className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold ${
                    activeTab === 'archives'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Archive className="h-4 w-4" />
                  Archives
                </button>
              </div>

              {activeTab === 'preparation' && (
                <>
                  <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
                <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-black text-slate-900">Ajouter un point à discuter</h3>
                      <p className="text-sm text-slate-500">Les nouveaux points commencent en statut À valider.</p>
                    </div>
                    {saving && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sauvegarde
                      </span>
                    )}
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="md:col-span-2">
                      <span className="text-xs font-bold uppercase text-slate-500">Sujet</span>
                      <input
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        placeholder="Ex. Suivi backups client, enjeu ressource, décision à prendre"
                      />
                    </label>
                    <label>
                      <span className="text-xs font-bold uppercase text-slate-500">Responsable</span>
                      <input
                        value={owner}
                        onChange={(event) => setOwner(event.target.value)}
                        className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        placeholder="Nom ou initiales"
                      />
                    </label>
                    <label>
                      <span className="text-xs font-bold uppercase text-slate-500">Journée ciblée</span>
                      <input
                        type="date"
                        value={selectedScrumDate}
                        onChange={(event) => setSelectedScrumDate(event.target.value)}
                        className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />
                    </label>
                    <label className="md:col-span-2">
                      <span className="text-xs font-bold uppercase text-slate-500">Détails</span>
                      <textarea
                        value={details}
                        onChange={(event) => setDetails(event.target.value)}
                        className="mt-1 min-h-24 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        placeholder="Contexte, décision attendue, lien avec un client ou un projet..."
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddPoint}
                    className="mt-4 inline-flex h-10 items-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-bold text-white shadow-sm hover:bg-blue-800"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter le point
                  </button>
                </section>

                <aside className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-lg font-black text-slate-900">Créer la journée du scrum</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    La journée regroupe les points marqués À ajouter au scrum pour la date choisie.
                  </p>
                  <input
                    type="date"
                    value={selectedScrumDate}
                    onChange={(event) => setSelectedScrumDate(event.target.value)}
                    className="mt-4 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <button
                    type="button"
                    onClick={createScrumDay}
                    className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-bold text-white hover:bg-slate-800"
                  >
                    <Save className="h-4 w-4" />
                    Créer / mettre à jour cette journée
                  </button>
                  <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                    Date active: <strong>{formatDateLabel(selectedScrumDate)}</strong>
                    {updatedBy && <div className="mt-1">Dernière sauvegarde par {updatedBy}</div>}
                  </div>
                </aside>
              </div>

                  <section className="space-y-4">
                  <h3 className="text-xl font-black text-slate-900">Points actifs</h3>
                  {visiblePoints.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                      Aucun point actif pour le moment.
                    </div>
                  ) : (
                    visiblePoints.map((point) => renderPointCard(point))
                  )}
                  </section>
                </>
              )}

              {activeTab === 'fonctionnement' && (
                <section className="space-y-5">
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold uppercase text-blue-800">
                          <Lightbulb className="h-3.5 w-3.5" />
                          Esprit de la tuile
                        </div>
                        <h3 className="mt-3 text-2xl font-black text-slate-950">
                          Préparer le lundi avant d’être rendu au lundi.
                        </h3>
                        <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-600">
                          Scrum-TI sert à centraliser les sujets opérationnels de la direction TI, à décider
                          ce qui mérite d’être porté au scrum du lundi et à garder une mémoire simple des
                          décisions, suivis et points non retenus.
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-950 px-4 py-3 text-sm font-bold text-white">
                        1 tuile, 1 rituel, 1 historique
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-3">
                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                        <Target className="h-5 w-5" />
                      </div>
                      <h4 className="mt-4 font-black text-slate-950">1. Capturer les points</h4>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        Ajouter les sujets dès qu’ils apparaissent : enjeux clients, décisions attendues,
                        suivis internes, blocages, risques, priorités ou éléments à clarifier.
                      </p>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                        <Workflow className="h-5 w-5" />
                      </div>
                      <h4 className="mt-4 font-black text-slate-950">2. Filtrer pour le scrum</h4>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        Les points commencent à valider. La direction choisit ensuite ce qui doit être ajouté
                        au scrum, ce qui reste en préparation et ce qui peut être archivé.
                      </p>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                        <History className="h-5 w-5" />
                      </div>
                      <h4 className="mt-4 font-black text-slate-950">3. Conserver la trace</h4>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        Chaque journée créée regroupe les points retenus. L’historique permet de retrouver
                        ce qui a été discuté, quand, et avec quel contexte.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h4 className="text-lg font-black text-slate-950">Règles d’utilisation</h4>
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        {[
                          'Un point doit être clair, court et actionnable.',
                          'Le responsable sert à orienter le suivi, pas à blâmer.',
                          'La date ciblée indique le scrum où le point devrait être discuté.',
                          'Les archives servent à garder le bruit hors du scrum actif.',
                        ].map((rule) => (
                          <div key={rule} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-700">
                            {rule}
                          </div>
                        ))}
                      </div>
                    </div>

                    <aside className="rounded-xl border border-blue-200 bg-blue-50 p-5 shadow-sm">
                      <h4 className="text-lg font-black text-slate-950">Résultat attendu</h4>
                      <p className="mt-2 text-sm leading-relaxed text-slate-700">
                        Le scrum du lundi arrive avec une liste déjà triée : moins d’improvisation, moins
                        d’oublis, plus de décisions prises au bon moment.
                      </p>
                      <div className="mt-4 rounded-lg bg-white p-3 text-xs font-semibold uppercase text-blue-800">
                        Préparation → Sélection → Scrum → Historique
                      </div>
                    </aside>
                  </div>
                </section>
              )}

              {activeTab === 'historique' && (
                <section className="space-y-4">
                  <h3 className="text-xl font-black text-slate-900">Historique des scrums</h3>
                  {sortedScrums.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                      Aucune journée de scrum créée.
                    </div>
                  ) : (
                    sortedScrums.map((scrum) => (
                      <div key={scrum.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h4 className="font-black text-slate-900">{scrum.title}</h4>
                            <p className="text-xs text-slate-500">{scrum.pointIds.length} point(s)</p>
                          </div>
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                            {formatDateLabel(scrum.date)}
                          </span>
                        </div>
                        <div className="mt-4 space-y-3">
                          {scrum.pointIds.length === 0 ? (
                            <p className="text-sm text-slate-500">Aucun point attaché à cette journée.</p>
                          ) : (
                            scrum.pointIds
                              .map((pointId) => pointsById[pointId])
                              .filter(Boolean)
                              .map((point) => renderPointCard(point, true))
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </section>
              )}

              {activeTab === 'archives' && (
                <section className="space-y-4">
                  <h3 className="text-xl font-black text-slate-900">Archives</h3>
                  {archivedPoints.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                      Aucun point archivé.
                    </div>
                  ) : (
                    <div className="grid gap-3 lg:grid-cols-2">
                      {archivedPoints.map((point) => renderPointCard(point))}
                    </div>
                  )}
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
